import merchantService from "../../services/api/merchantService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_CONFIGS, STALE_TIMES } from "../../lib/react-query/config";
import { POLL_INTERVAL_MS } from "../../components/FundsTransfer/config/fundsTransferConfig";

export const merchantQueryKeys = {
  all: ["merchants"],
  profile: () => [...merchantQueryKeys.all, "profile"],
  profileById: (merchantId) => [
    ...merchantQueryKeys.all,
    "profile",
    merchantId,
  ],
  accounts: () => [...merchantQueryKeys.all, "accounts"],
  mobileMoneyAccount: () => [...merchantQueryKeys.all, "mobile-money-account"],
  bankAccount: () => [...merchantQueryKeys.all, "bank-account"],
  walletAccount: () => [...merchantQueryKeys.all, "wallet-account"],
  createMerchantInstitution: () => [
    ...merchantQueryKeys.all,
    "create-merchant-institution",
  ],
  editMerchantInstitution: () => [
    ...merchantQueryKeys.all,
    "edit-merchant-institution",
  ],
  editCurrentMerchantInstitution: () => [
    ...merchantQueryKeys.all,
    "edit-current-merchant-institution",
  ],
  updateMerchantLogo: () => [...merchantQueryKeys.all, "update-merchant-logo"],
  toggleMerchantStatus: () => [
    ...merchantQueryKeys.all,
    "toggle-merchant-status",
  ],
  merchantBalance: () => [...merchantQueryKeys.all, "merchant-balance"],
  economicSectors: () => [...merchantQueryKeys.all, "economic-sectors"],
  productTransactionCodes: () => [
    ...merchantQueryKeys.all,
    "product-transaction-codes",
  ],
  productOwnerships: () => [...merchantQueryKeys.all, "product-ownerships"],
  productCategories: () => [...merchantQueryKeys.all, "product-categories"],
  genders: () => [...merchantQueryKeys.all, "genders"],
  merchantDisbursementsCharges: () => [
    ...merchantQueryKeys.all,
    "merchant-disbursements-charges",
  ],
  merchantDisbursementsInitiateOtp: () => [
    ...merchantQueryKeys.all,
    "merchant-disbursements-initiate-otp",
  ],
  merchantDisbursementsResendOtp: () => [
    ...merchantQueryKeys.all,
    "merchant-disbursements-resend-otp",
  ],
  merchantDisbursementsAuthorize: () => [
    ...merchantQueryKeys.all,
    "merchant-disbursements-authorize",
  ],
  disbursement: (transactionId) => [
    ...merchantQueryKeys.all,
    "disbursement",
    transactionId,
  ],
  features: () => [...merchantQueryKeys.all, "features"],
};

export const useGetMerchantProfile = () => {
  return useQuery({
    queryKey: merchantQueryKeys.profile(),
    queryFn: merchantService.getCurrentMerchantProfile,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetMerchantProfileById = (merchantId) => {
  return useQuery({
    queryKey: merchantQueryKeys.profileById(merchantId),
    queryFn: () => merchantService.getMerchantProfileById(merchantId),
    ...QUERY_CONFIGS.STATIC,
    enabled: !!merchantId,
  });
};

export const useGetMerchantPaymentAccounts = () => {
  return useQuery({
    queryKey: merchantQueryKeys.accounts(),
    queryFn: merchantService.getMerchantPaymentAccounts,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useCreateMobileMoneyAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountData) =>
      merchantService.addMobileMoneyAccount(accountData),
    mutationKey: merchantQueryKeys.mobileMoneyAccount(),
    onMutate: async (newAccount) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
      const previousAccounts = queryClient.getQueryData(
        merchantQueryKeys.accounts(),
      );

      queryClient.setQueryData(
        merchantQueryKeys.accounts(),
        (currentAccounts) => {
          if (!currentAccounts) return currentAccounts;

          return {
            ...currentAccounts,
            mobileMoneyAccounts: [
              ...(currentAccounts.mobileMoneyAccounts ?? []),
              {
                ...newAccount,
                id: crypto.randomUUID(),
                active: true,
              },
            ],
          };
        },
      );

      return { previousAccounts };
    },
    onError: (error, _newAccount, context) => {
      if (context?.previousAccounts) {
        queryClient.setQueryData(
          merchantQueryKeys.accounts(),
          context.previousAccounts,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
  });
};

export const useCreateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountData) => merchantService.addBankAccount(accountData),
    mutationKey: merchantQueryKeys.bankAccount(),
    onMutate: async (newAccount) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
      const previousAccounts = queryClient.getQueryData(
        merchantQueryKeys.accounts(),
      );

      queryClient.setQueryData(
        merchantQueryKeys.accounts(),
        (currentAccounts) => {
          if (!currentAccounts) return currentAccounts;

          return {
            ...currentAccounts,
            bankAccounts: [
              ...(currentAccounts.bankAccounts ?? []),
              {
                ...newAccount,
                id: crypto.randomUUID(),
                active: true,
              },
            ],
          };
        },
      );

      return { previousAccounts };
    },
    onError: (error, _newAccount, context) => {
      if (context?.previousAccounts) {
        queryClient.setQueryData(
          merchantQueryKeys.accounts(),
          context.previousAccounts,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
  });
};

export const useCreatePaykitWalletAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountData) => merchantService.addPaykitWallet(accountData),
    mutationKey: merchantQueryKeys.walletAccount(),
    onMutate: async (newAccount) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
      const previousAccounts = queryClient.getQueryData(
        merchantQueryKeys.accounts(),
      );

      queryClient.setQueryData(
        merchantQueryKeys.accounts(),
        (currentAccounts) => {
          if (!currentAccounts) return currentAccounts;

          return {
            ...currentAccounts,
            walletAccounts: [
              ...(currentAccounts.walletAccounts ?? []),
              {
                ...newAccount,
                id: crypto.randomUUID(),
                active: true,
              },
            ],
          };
        },
      );

      return { previousAccounts };
    },
    onError: (error, _newAccount, context) => {
      if (context?.previousAccounts) {
        queryClient.setQueryData(
          merchantQueryKeys.accounts(),
          context.previousAccounts,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.accounts(),
      });
    },
  });
};

export const useGetAllMerchants = (options = {}) => {
  const enabled =
    typeof options?.enabled === "boolean" ? options.enabled : true;

  return useQuery({
    queryKey: merchantQueryKeys.all,
    queryFn: merchantService.getAllMerchants,
    enabled,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useCreateMerchantInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (merchantData) =>
      merchantService.createMerchantInstitution(merchantData),
    mutationKey: merchantQueryKeys.createMerchantInstitution(),
    onMutate: async (newMerchant) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.all,
      });
      const previousMerchants = queryClient.getQueryData(merchantQueryKeys.all);

      queryClient.setQueryData(
        merchantQueryKeys.all,
        (currentMerchantsList) => {
          if (!Array.isArray(currentMerchantsList)) return currentMerchantsList;

          return [
            { ...newMerchant, id: crypto.randomUUID(), optimistic: true },
            ...currentMerchantsList,
          ];
        },
      );

      return { previousMerchants };
    },
    onError: (error, _newMerchant, context) => {
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          merchantQueryKeys.all,
          context.previousMerchants,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.all,
      });
    },
  });
};

export const useEditMerchantInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, merchantData }) =>
      merchantService.editMerchantInstitution(merchantId, merchantData),
    mutationKey: merchantQueryKeys.editMerchantInstitution(),

    onMutate: async ({ merchantId, merchantData }) => {
      await queryClient.cancelQueries({ queryKey: merchantQueryKeys.all });
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profile(),
      });
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profileById(merchantId),
      });

      const previousMerchants = queryClient.getQueryData(merchantQueryKeys.all);
      const previousProfile = queryClient.getQueryData(
        merchantQueryKeys.profile(),
      );
      const previousProfileById = queryClient.getQueryData(
        merchantQueryKeys.profileById(merchantId),
      );

      queryClient.setQueryData(
        merchantQueryKeys.all,
        (currentMerchantsList) => {
          if (!Array.isArray(currentMerchantsList)) return currentMerchantsList;
          return currentMerchantsList.map((merchant) =>
            merchant.id === merchantId
              ? { ...merchant, ...merchantData }
              : merchant,
          );
        },
      );
      queryClient.setQueryData(
        merchantQueryKeys.profile(),
        (currentProfile) => {
          if (!currentProfile) return currentProfile;
          return { ...currentProfile, ...merchantData };
        },
      );
      queryClient.setQueryData(
        merchantQueryKeys.profileById(merchantId),
        (currentMerchantProfile) => {
          if (!currentMerchantProfile) return currentMerchantProfile;
          return { ...currentMerchantProfile, ...merchantData };
        },
      );

      return { previousMerchants, previousProfile, previousProfileById };
    },

    onError: (error, variables, context) => {
      const { merchantId } = variables ?? {};

      if (context?.previousMerchants) {
        queryClient.setQueryData(
          merchantQueryKeys.all,
          context.previousMerchants,
        );
      }
      if (context?.previousProfile) {
        queryClient.setQueryData(
          merchantQueryKeys.profile(),
          context.previousProfile,
        );
      }
      if (context?.previousProfileById && merchantId) {
        queryClient.setQueryData(
          merchantQueryKeys.profileById(merchantId),
          context.previousProfileById,
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      const { merchantId } = variables ?? {};

      queryClient.invalidateQueries({ queryKey: merchantQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: merchantQueryKeys.profile() });
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: merchantQueryKeys.profileById(merchantId),
        });
      }
    },
    retry: false,
  });
};

export const useEditCurrentMerchantInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (merchantData) =>
      merchantService.editCurrentMerchantInstitution(merchantData),
    mutationKey: merchantQueryKeys.editCurrentMerchantInstitution(),
    onMutate: async (newMerchantData) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profile(),
      });

      const previousProfile = queryClient.getQueryData(
        merchantQueryKeys.profile(),
      );

      queryClient.setQueryData(
        merchantQueryKeys.profile(),
        (currentProfile) => {
          if (!currentProfile) return currentProfile;

          return {
            ...currentProfile,
            ...newMerchantData,
          };
        },
      );

      return { previousProfile };
    },
    onError: (error, _newMerchantData, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(
          merchantQueryKeys.profile(),
          context.previousProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.profile(),
      });
    },
    retry: false,
  });
};

export const useCreateMerchantInstitutionMobileMoneyAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, accountData }) =>
      merchantService.addMerchantInstitutionMobileMoneyAccount(
        merchantId,
        accountData,
      ),
    mutationKey: merchantQueryKeys.mobileMoneyAccount(),
    onMutate: async ({ merchantId, accountData }) => {
      await queryClient.cancelQueries({ queryKey: merchantQueryKeys.all });
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profileById(merchantId),
      });

      const previousMerchants = queryClient.getQueryData(merchantQueryKeys.all);
      const previousProfileById = queryClient.getQueryData(
        merchantQueryKeys.profileById(merchantId),
      );

      queryClient.setQueryData(
        merchantQueryKeys.profileById(merchantId),
        (currentMerchantProfile) => {
          if (!currentMerchantProfile) return currentMerchantProfile;

          return {
            ...currentMerchantProfile,
            mobileMoneyAccounts: [
              ...(currentMerchantProfile.mobileMoneyAccounts ?? []),
              {
                ...accountData,
                id: crypto.randomUUID(),
                active: true,
              },
            ],
          };
        },
      );

      return { previousMerchants, previousProfileById };
    },
    onError: (error, vars, context) => {
      const { merchantId } = vars ?? {};

      if (context?.previousMerchants) {
        queryClient.setQueryData(
          merchantQueryKeys.all,
          context.previousMerchants,
        );
      }
      if (context?.previousProfileById && merchantId) {
        queryClient.setQueryData(
          merchantQueryKeys.profileById(merchantId),
          context.previousProfileById,
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      const merchantId = variables?.merchantId;
      queryClient.invalidateQueries({ queryKey: merchantQueryKeys.all });
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: merchantQueryKeys.profileById(merchantId),
        });
      }
    },
  });
};

export const useUpdateMerchantLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => merchantService.updateMerchantLogo(data),
    mutationKey: merchantQueryKeys.updateMerchantLogo(),

    onMutate: async (newLogo) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profile(),
      });
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.profileById(newLogo.merchantId),
      });

      const previousProfile = queryClient.getQueryData(
        merchantQueryKeys.profile(),
      );
      const previousProfileById = queryClient.getQueryData(
        merchantQueryKeys.profileById(newLogo.merchantId),
      );

      queryClient.setQueryData(
        merchantQueryKeys.profile(),
        (currentProfile) => {
          if (!currentProfile) return currentProfile;

          return {
            ...currentProfile,
            logoBase64: newLogo.logo,
          };
        },
      );
      queryClient.setQueryData(
        merchantQueryKeys.profileById(newLogo.merchantId),
        (currentMerchantProfile) => {
          if (!currentMerchantProfile) return currentMerchantProfile;

          return {
            ...currentMerchantProfile,
            logoBase64: newLogo.logo,
          };
        },
      );

      return { previousProfile, previousProfileById };
    },

    onError: (error, variables, context) => {
      const { merchantId } = variables ?? {};

      if (context?.previousProfile) {
        queryClient.setQueryData(
          merchantQueryKeys.profile(),
          context.previousProfile,
        );
      }
      if (context?.previousProfileById && merchantId) {
        queryClient.setQueryData(
          merchantQueryKeys.profileById(merchantId),
          context.previousProfileById,
        );
      }
    },

    onSuccess: (_data, _error, variables) => {
      const { merchantId } = variables ?? {};

      queryClient.invalidateQueries({ queryKey: merchantQueryKeys.profile() });
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: merchantQueryKeys.profileById(merchantId),
        });
      }
    },

    onSettled: (_data, _error, variables) => {
      const { merchantId } = variables ?? {};

      queryClient.invalidateQueries({ queryKey: merchantQueryKeys.profile() });
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: merchantQueryKeys.profileById(merchantId),
        });
      }
    },
  });
};

export const useToggleMerchantStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ merchantId, actionType }) =>
      merchantService.toggleMerchantStatus(merchantId, actionType),
    mutationKey: merchantQueryKeys.toggleMerchantStatus(),
    onMutate: async ({ merchantId, actionType }) => {
      await queryClient.cancelQueries({
        queryKey: merchantQueryKeys.all,
      });

      const previousMerchants = queryClient.getQueryData(merchantQueryKeys.all);

      queryClient.setQueryData(
        merchantQueryKeys.all,
        (currentMerchantsList) => {
          if (!Array.isArray(currentMerchantsList)) return currentMerchantsList;

          return currentMerchantsList.map((merchant) =>
            merchant.merchantId === merchantId
              ? {
                  ...merchant,
                  accountStatus: actionType === "ACTIVATE",
                }
              : merchant,
          );
        },
      );

      return { previousMerchants };
    },

    onError: (error, _vars, context) => {
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          merchantQueryKeys.all,
          context.previousMerchants,
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: merchantQueryKeys.all,
      });
    },
  });
};

export const useGetMerchantWalletBalance = () => {
  return useQuery({
    queryKey: merchantQueryKeys.merchantBalance(),
    queryFn: merchantService.getMerchantWalletBalance,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useGetEconomicSectors = () => {
  return useQuery({
    queryKey: merchantQueryKeys.economicSectors(),
    queryFn: merchantService.getEconomicSectors,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetProductTransactionCodes = () => {
  return useQuery({
    queryKey: merchantQueryKeys.productTransactionCodes(),
    queryFn: merchantService.getProductTransactionCodes,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetProductOwnerships = () => {
  return useQuery({
    queryKey: merchantQueryKeys.productOwnerships(),
    queryFn: merchantService.getProductOwnerships,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetProductCategories = () => {
  return useQuery({
    queryKey: merchantQueryKeys.productCategories(),
    queryFn: merchantService.getProductCategories,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetGenders = () => {
  return useQuery({
    queryKey: merchantQueryKeys.genders(),
    queryFn: merchantService.getGenders,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useMerchantDisbursementsCharges = () => {
  return useMutation({
    mutationFn: (payload) =>
      merchantService.getMerchantDisbursementsCharges(payload),
    mutationKey: merchantQueryKeys.merchantDisbursementsCharges(),
  });
};

export const useMerchantInitiateWithdrawal = () => {
  return useMutation({
    mutationFn: (payload) =>
      merchantService.initiateMerchantWithdrawal(payload),
    mutationKey: merchantQueryKeys.merchantDisbursementsInitiateOtp(),
  });
};

export const useResendDisbursementOtp = () => {
  return useMutation({
    mutationFn: (transactionId) =>
      merchantService.resendDisbursementOtp(transactionId),
    mutationKey: merchantQueryKeys.merchantDisbursementsResendOtp(),
  });
};

export const useAuthorizeDisbursement = () => {
  return useMutation({
    mutationFn: ({ otpCode }) =>
      merchantService.authorizeDisbursement({ otpCode }),
    mutationKey: merchantQueryKeys.merchantDisbursementsAuthorize(),
  });
};

export const useGetDisbursement = (
  transactionId,
  { enablePolling = true } = {},
) => {
  return useQuery({
    queryKey: merchantQueryKeys.disbursement(transactionId),
    queryFn: () => merchantService.getDisbursement(transactionId),
    enabled: !!transactionId,
    staleTime: 0,
    retry: false,
    refetchInterval: enablePolling ? POLL_INTERVAL_MS : false,
  });
};

export const useGetMerchantFeatureFlags = () => {
  return useQuery({
    queryKey: merchantQueryKeys.features(),
    queryFn: merchantService.getMerchantFeatureFlags,
    staleTime: STALE_TIMES.NORMAL,
    retry: false,
  });
};
